import type React from "react"
import { useMemo, useState } from "react"
import { useVariantsByIds } from "~/hooks/useVariants"
import { Button } from "../reusable/Button"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogDescription,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../reusable/Dialog"
import { ClockCountdown } from "@phosphor-icons/react"
import DatePicker from "../reusable/DatePicker"
import NpiGraph from "./ActivateVariantDialog/NpiGraph"
import { getStartOfCurrentWeekUTC, isMondayDatePicker, startOfDayUTC } from "~/utils/datetime"
import { addWeeks, min } from "date-fns"
import { useMutateActivateSkus } from "~/hooks/useManageSkus"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import { Label } from "../reusable/Label"
import { type InboxRecommendationType } from "~/constants/npi"
import { ruleCopy, type RuleCopy } from "~/utils/inbox-recommendations/rules"
import { replaceName } from "~/utils/namingUtils"
import { recommendationDescriptors } from "./recommendations/descriptors"

export type ReactivateVariantData = {
  isNpi?: boolean
  activatedFrom?: Date | string | null
}

type Props = {
  selectedVariantIds: string[]
  variantData?: ReactivateVariantData[]
  trigger?: React.ReactNode
  disabled?: boolean
  disabledTooltip?: string
  suppressTooltip?: boolean
  recommendation?: InboxRecommendationType
  onSuccess?: () => void
}

export default function ReactivateSkusDialog({
  selectedVariantIds,
  variantData,
  trigger: customTrigger,
  disabled,
  disabledTooltip,
  suppressTooltip,
  recommendation,
  onSuccess,
}: Props) {
  const copy: RuleCopy | undefined = recommendation ? ruleCopy[recommendation] : undefined
  const title = recommendation
    ? recommendationDescriptors[recommendation].label
    : "Change activation date"
  const [open, setOpen] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const storeVariants = useVariantsByIds(selectedVariantIds)

  const resolvedData = useMemo(() => {
    if (variantData && variantData.length > 0) return variantData
    return storeVariants.map((x) => ({
      isNpi: x.isNpi,
      activatedFrom: x.settings?.activatedFrom,
    }))
  }, [variantData, storeVariants])

  const isNpiVariants = resolvedData.every((x) => x?.isNpi !== false)

  const existingDates = filterNotUndefinedOrNull(resolvedData.map((x) => x.activatedFrom))
  const fallbackStartDate = addWeeks(getStartOfCurrentWeekUTC(), 1)
  const currentStartDate =
    existingDates.length > 0 ? startOfDayUTC(min(existingDates)) : fallbackStartDate

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next && isPending) return
        setOpen(next)
      }}
    >
      <DialogTrigger
        asChild
        requires="edit"
        allowedTitle={
          suppressTooltip
            ? undefined
            : (disabledTooltip ??
              (!isNpiVariants
                ? "One or more selected variants are not NPI variants. Only NPI variants can have their activation date changed."
                : undefined))
        }
        disabled={disabled || !isNpiVariants}
      >
        {customTrigger ?? (
          <Button size="sm" variant="neutral">
            <ClockCountdown />
            Change activation date
          </Button>
        )}
      </DialogTrigger>
      <DialogContentV2 className="max-w-[65vw]">
        <DialogHeaderV2>
          <DialogTitle>{title}</DialogTitle>
          {copy && (
            <DialogDescription className="mt-1">
              {replaceName(copy.customerSummary)}
            </DialogDescription>
          )}
        </DialogHeaderV2>
        <ReactivateSkusContent
          variantIds={selectedVariantIds}
          setOpen={setOpen}
          setIsPending={setIsPending}
          currentStartDate={currentStartDate}
          onSuccess={onSuccess}
        />
      </DialogContentV2>
    </DialogRoot>
  )
}

const ReactivateSkusContent = ({
  variantIds,
  setOpen,
  setIsPending,
  currentStartDate,
  onSuccess,
}: {
  variantIds: string[]
  setOpen: (open: boolean) => void
  setIsPending: (pending: boolean) => void
  currentStartDate: Date
  onSuccess?: () => void
}) => {
  const minDate: Date = addWeeks(getStartOfCurrentWeekUTC(), 1)
  const [date, setDate] = useState(currentStartDate)

  const activateSkus = useMutateActivateSkus(() => {
    setIsPending(false)
    setOpen(false)
    onSuccess?.()
  })
  return (
    <>
      <DialogBodyV2>
        <div className="flex flex-col gap-1">
          <Label>When to start forecasting</Label>
          <DatePicker
            filterDate={isMondayDatePicker}
            value={date}
            onChange={(day) => day && setDate(day)}
            minDate={minDate}
          />
        </div>
        <NpiGraph variantIds={variantIds} startDate={date} />
      </DialogBodyV2>
      <DialogFooterV2 className="justify-end">
        <Button
          onClick={() => {
            setIsPending(true)
            activateSkus.mutate({
              activatedFrom: date,
              variantIds,
              reactivate: true,
            })
          }}
          loading={activateSkus.isPending}
        >
          {activateSkus.isPending
            ? "Changing"
            : `Change for ${variantIds.length} ${variantIds.length > 1 ? "variants" : "variant"}`}
        </Button>
      </DialogFooterV2>
    </>
  )
}
